/**
 * 音声素材 (BGM / ナレーション / サウンドロゴ) の生成プロバイダ・生成モデル ID を
 * 人間向け表記へ変換する共通ヘルパー。
 *
 * org-admin の素材詳細モーダルとユーザー側の生成情報モーダルの両方で使う。
 * generation_metadata の `provider` (elevenlabs / lyria / lyria-pro / gemini) と
 * `model` (無ければ旧レコードの `model_id`) を読む前提。
 */

const PROVIDER_LABELS: Record<string, string> = {
  elevenlabs: 'ElevenLabs',
  lyria: 'Gemini (Lyria)',
  'lyria-pro': 'Gemini (Lyria)',
  gemini: 'Gemini',
};

export function audioProviderLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  return PROVIDER_LABELS[code] ?? code;
}

/** 音声モデルらしい ID か (音声素材以外で誤ってこのラベラーを当てないための判定補助) */
export function isAudioModelCode(code: string | null | undefined): boolean {
  if (!code) return false;
  const lower = code.toLowerCase();
  return lower.startsWith('lyria') || lower.startsWith('eleven') || lower.startsWith('music_v') || lower.includes('-tts');
}

/**
 * 音声モデル ID → 表示名。
 *   lyria-3.5                    → "Lyria 3.5"
 *   lyria-3-clip-preview         → "Lyria 3 Clip"
 *   lyria-3-pro-preview          → "Lyria 3 Pro"
 *   music_v1                     → "ElevenLabs Music v1"
 *   music_v2_5                   → "ElevenLabs Music v2.5"
 *   eleven_multilingual_v2       → "ElevenLabs Multilingual v2"
 *   gemini-3.1-flash-tts-preview → "Gemini 3.1 Flash TTS"
 */
export function audioModelLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  const lower = code.toLowerCase();
  const cap = (w: string) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w);
  const words = (rest: string) =>
    rest
      .split(/[-_]/)
      .filter((w) => w && w !== 'preview')
      .map((w) => (w === 'tts' ? 'TTS' : /^v\d+$/.test(w) ? w : cap(w)));

  if (lower.startsWith('lyria')) {
    return ['Lyria', ...words(code.slice('lyria'.length))].join(' ');
  }
  if (lower.startsWith('music_v')) {
    // v2_5 → v2.5 (ElevenLabs はマイナー版を _ で表す)
    return `ElevenLabs Music ${code.slice('music_'.length).replace(/_/g, '.')}`;
  }
  if (lower.startsWith('eleven_') || lower.startsWith('eleven-')) {
    return ['ElevenLabs', ...words(code.slice('eleven_'.length))].join(' ');
  }
  if (lower.startsWith('gemini-')) {
    return ['Gemini', ...words(code.slice('gemini-'.length))].join(' ');
  }
  return words(code).join(' ');
}

/** 「モデル名 (プロバイダ)」の 1 行表記。プロバイダがモデル名に含まれるときは省く。 */
export function audioModelWithProviderLabel(
  modelCode: string | null | undefined,
  provider: string | null | undefined,
): string | null {
  const model = audioModelLabel(modelCode);
  const prov = audioProviderLabel(provider);
  if (!model) return prov;
  if (!prov) return model;
  const provHead = prov.split(' ')[0];
  if (model.startsWith(provHead) || model.startsWith('Lyria')) return model;
  return `${model} (${prov})`;
}
